import { ENTITY_PREFIX, Roles } from '../constants';
import { HistorySearchParams } from '../utils/common';

export const DATA_DASHBOARD_DATE_PRESETS = [
  { value: 'last_week', label: 'Last week', days: 7 },
  { value: 'last_month', label: 'Last month', days: 30 },
  { value: 'last_3_months', label: 'Last 3 months', days: 90 },
  { value: 'last_6_months', label: 'Last 6 months', days: 180 },
  { value: 'last_12_months', label: 'Last 12 months', days: 365 },
] as const;

export const QA_DASHBOARD_WEEK_DATE_LS_KEY = 'qaWeekDate';

export const ALL_CLIENTS_ID = 'all';

export const ClaimTargetIdPrefixMappings = {
  [HistorySearchParams.ClaimId]: ENTITY_PREFIX.Claim,
  [HistorySearchParams.ClientSupervisionId]: ENTITY_PREFIX.ClientSupervisionNotes,
  [HistorySearchParams.ParentSupervisionId]: ENTITY_PREFIX.ParentSupervisionNotes,
  [HistorySearchParams.GroupParentSupervisionId]: ENTITY_PREFIX.GroupSupervisionNotes,
  [HistorySearchParams.ClientAssessmentId]: ENTITY_PREFIX.ClientAssessmentNote,
  [HistorySearchParams.SessionId]: ENTITY_PREFIX.SessionNotes,
};

export enum ProviderStatusEnum {
  TRAINING = 'Training',
  NOT_TRAINING = 'Not Training',
}

export enum ClaimSourceEnum {
  APP = 'APP',
  RETOOL_LINK = 'RETOOL_LINK',
}

export const ClaimSourceLabels = {
  APP: 'App',
  RETOOL_LINK: 'Retool link',
};

export enum HistoricalDocuments {
  Claim = 'claim',
  Assessment = 'assessment',
  BTSupervision = 'btSupervision',
  ClientSupervision = 'clientSupervision',
  GroupSupervision = 'groupParentSupervision',
  ParentSupervision = 'parentSupervision',
  Session = 'session',
  Invoice = 'invoice',
  Questionnaire = 'questionnaire',
}

export const HistoricalDocumentOptions = [
  {
    id: HistoricalDocuments.Claim,
    label: 'Claim',
    rolesWithAccess: [Roles.Admin, Roles.Biller, Roles.InvoiceCoordinator],
  },
  {
    id: HistoricalDocuments.Assessment,
    label: 'Assessment - 97151 / 97152',
    rolesWithAccess: [
      Roles.Admin,
      Roles.AssistantAssessor,
      Roles.Bcba,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.ClientSuccessManager,
    ],
  },
  {
    id: HistoricalDocuments.BTSupervision,
    label: 'BT supervision',
    rolesWithAccess: [
      Roles.Admin,
      Roles.AssistantAssessor,
      Roles.Bcba,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.ClientSuccessManager,
      Roles.QaSpecialist,
    ],
  },
  {
    id: HistoricalDocuments.ClientSupervision,
    label: 'Client supervision - 97155',
    rolesWithAccess: [
      Roles.Admin,
      Roles.AssistantAssessor,
      Roles.Bcba,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.ClientSuccessManager,
      Roles.QaSpecialist,
      Roles.LimitedAccessGuardian,
    ],
  },
  {
    id: HistoricalDocuments.GroupSupervision,
    label: 'Group parent training - 97157',
    rolesWithAccess: [
      Roles.Admin,
      Roles.Bcba,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.ClientSuccessManager,
    ],
  },
  {
    id: HistoricalDocuments.ParentSupervision,
    label: 'Parent supervision - 97156',
    rolesWithAccess: [
      Roles.Admin,
      Roles.AssistantAssessor,
      Roles.Bcba,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.ClientSuccessManager,
      Roles.QaSpecialist,
    ],
  },
  {
    id: HistoricalDocuments.Session,
    label: 'Session notes - 97153',
    rolesWithAccess: [
      Roles.Admin,
      Roles.AssistantAssessor,
      Roles.Bcba,
      Roles.Guardian,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.ClientSuccessManager,
      Roles.QaSpecialist,
      Roles.LimitedAccessGuardian,
    ],
  },
  {
    id: HistoricalDocuments.Invoice,
    label: 'Invoice',
    rolesWithAccess: [
      Roles.Admin,
      Roles.Biller,
      Roles.InvoiceCoordinator,
      Roles.ClientSuccessManager,
      Roles.LimitedAccessGuardian,
    ],
  },
  {
    id: HistoricalDocuments.Questionnaire,
    label: 'Questionnaire',
    rolesWithAccess: [Roles.Admin, Roles.Guardian, Roles.Bcba, Roles.ClientSuccessManager],
  },
];

// One unit is 900 seconds (15 minutes)
export const PROCEDURE_DURATION_TO_UNIT_FACTOR = 900;

export const TIMESHEET_OPTIONS = [
  {
    label: 'Non-Therapy Hours',
    rolesWithAccess: [Roles.Prbt, Roles.Vbt],
  },
  {
    label: 'On-Call',
    rolesWithAccess: [Roles.Vbt],
    isClientOptional: true,
  },
  {
    label: '2.6 Initial Care Coordination',
    rolesWithAccess: [],
  },
  {
    label: 'Client no-show',
    rolesWithAccess: [],
  },
  {
    label: 'Collateral Care Coordination',
    rolesWithAccess: [],
  },
  {
    label: 'Contracted',
    rolesWithAccess: [],
  },
  {
    label: 'D/C Planning',
    rolesWithAccess: [],
  },
  {
    label: 'Emails',
    rolesWithAccess: [],
  },
  {
    label: 'Initial TP Review Call',
    rolesWithAccess: [],
  },
  {
    label: 'Internal Forta Meeting',
    rolesWithAccess: [],
  },
  {
    label: 'Meeting with 2.6 VBT',
    rolesWithAccess: [],
  },
  {
    label: 'Reassessment Meeting',
    rolesWithAccess: [],
  },
  {
    label: 'Reassessment TP Creation',
    rolesWithAccess: [],
  },
  {
    label: 'Reauth TP Review Call',
    rolesWithAccess: [],
  },
  {
    label: 'Scheduling',
    rolesWithAccess: [],
  },
  {
    label: 'TP Updates',
    rolesWithAccess: [Roles.Bcba],
  },
  {
    label: 'Licensing',
    rolesWithAccess: [Roles.Bcba],
    isClientOptional: true,
  },
  {
    label: 'Training',
    rolesWithAccess: [Roles.Bcba, Roles.Vbt],
    isClientOptional: true,
    eligibility: [ProviderStatusEnum.TRAINING],
  },
  {
    label: 'Shadowing',
    rolesWithAccess: [Roles.Bcba, Roles.Vbt],
    isClientOptional: true,
  },
  {
    label: 'Care coordination',
    rolesWithAccess: [Roles.Vbt],
    isClientOptional: true,
  },
  {
    label: 'Wait time',
    rolesWithAccess: [Roles.Vbt],
    isClientOptional: true,
  },
  {
    label: 'Meeting',
    rolesWithAccess: [Roles.Vbt],
    isClientOptional: true,
  },
  {
    label: 'Treatment Plan Development',
    rolesWithAccess: [Roles.Bcba],
  },
  {
    label: 'Bobbing',
    rolesWithAccess: [],
  },
];

export const NOT_AVAILABLE = 'N/A';

export const TARGET_STAGE_LOG_PHASE_CHANGE_TYPE = 'phase-change-from-target-stage';

export enum DurationChartPlotMethod {
  AVERAGE = 'AVERAGE',
  TOTAL = 'TOTAL',
}

export const DurationChartPlotOptions = [
  {
    value: DurationChartPlotMethod.AVERAGE,
    label: 'Average duration',
  },
  {
    value: DurationChartPlotMethod.TOTAL,
    label: 'Total duration',
  },
];

export const targetDescriptionBlacklist = [
  'tooth brushing',
  'bathroom',
  'coping',
  'breathing',
  'hairbrushing',
  'deep-breathing',
  'joint compression',
  'reading',
  'writing',
  'academic',
];

export const SupervisionMetricsPeriod = {
  Month: 'month',
  Week: 'week',
};

export const EVV_FALLBACK_REASONS = {
  NO_PHONE: 'no_phone',
  CAMERA_NOT_WORKING: 'camera_not_working',
  BATTERY_DEAD: 'battery_dead',
} as const;

export const EVV_FALLBACK_REASON_LABELS = {
  [EVV_FALLBACK_REASONS.NO_PHONE]: "I don't have my phone with me",
  [EVV_FALLBACK_REASONS.CAMERA_NOT_WORKING]: "My phone camera isn't working",
  [EVV_FALLBACK_REASONS.BATTERY_DEAD]: 'My phone battery is dead',
} as const;
